import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { Form, Formik } from 'formik';
import styled from "@emotion/styled";
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DialogTitle from '@mui/material/DialogTitle';
import InputLabel from '@mui/material/InputLabel';
import { toast } from 'react-toastify';
import Button from "components/widgets/Button";
import TextField from 'components/widgets/TextField';
import Spinner from "components/Spinner";
import Select from 'components/widgets/Select';
import { makeOffer } from 'data/vehicle.actions';
import NumberFormatCustom from "components/widgets/NumberFormatCustom";
import { permittedBuyRooftops } from "utils/User";

const PREFIX = 'offerDialog';
const GRID_PREFIX = 'gridOfferDailog';

const classes = {
    closeButton: `${PREFIX}-closeButton`,
    transportProviderSelect: `${PREFIX}-transportProviderSelect`,
    bgCard: `${PREFIX}-bgCard`,
    bottomSpace: `${GRID_PREFIX}-bottopSpace`,
    btn: `${PREFIX}-btn`
};

const StyledDialog = styled(Dialog)((
    {
        theme
    }
) => {
    return {

        [`& .${classes.closeButton}`]: {
            float: 'right',
            cursor: 'pointer'
        },
        [`& .${classes.transportProviderSelect}`]: {
            width: 410,
        },
        [`& .${classes.bgCard}`]: {
            marginTop: 40,
            padding: 30,
            borderRadius: 20,
            background: theme.palette.secondary.grey,
        },
        [`& .${classes.btn}`]: {
            marginTop: 30,
            background: '#213e97',
            borderRadius: 7,
            width: '100%',
            height: 50,
            textTransform: 'capitalize',
            marginBottom: 40
        }
    }
});

const StyledGrid = styled(Grid)((
    {
        theme
    }
) => {
    return {
        marginBottom: 20,
        [`& .${classes.bottomSpace}`]: {
            marginBottom: 30,
        },
    }
});

const validationSchema = yup.object().shape({
    offer: yup.string().when(['minimunOffer'], (minimunOffer) => {
        if (yup.number()) {
            return yup.number().integer()
                .required("Your Offer is required")
                .moreThan(minimunOffer, 'Minimum offer has been set to 80% of the asking price')
        }
    }),
    transProvider: yup.string().required("Transport Provider is required"),
});

const MakeOffer = ({ isOpen, handleClose, vehicle = {}, transportProvider }) => {
    const { rooftopsForUser } = useSelector(state => state.location)
    const { user } = useSelector(state => state.authentication);
    const { token, title } = vehicle;
    const offerPrice = (vehicle.price * 80) / 100;
    const auction = {}
    const permittedRooftops = permittedBuyRooftops(user, vehicle, auction, rooftopsForUser)

    let initialValues = {
        minimunOffer: offerPrice,
        offer: offerPrice,
        transProvider: 'transport-reinvent-txn_transport_flat_fee',
        buyerRooftopId: parseInt(user?.rooftop_id)
    };

    const dispatch = useDispatch();
    const {
        search: { submittingMakeOffer, offerSent, loadingShippingOptions, errorMakeOffer },
    } = useSelector(state => state);

    useEffect(() => {
        if (offerSent && offerSent === vehicle?.token) {
            toast.success('Offer sent successfully!');
            handleClose();
        }
        if (errorMakeOffer) {
            handleClose();
        }
    }, [offerSent, errorMakeOffer]);

    const handleSubmit = async (values) => {
        dispatch(makeOffer(values));
    }

    return (
        <StyledDialog
            open={isOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { width: "80%" } }}
        >
            <DialogTitle id="alert-dialog-title">
                <Box
                    onClick={() => handleClose()}
                    className={classes.closeButton}
                >
                    <Typography variant="h4">
                        X
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" className="pl-10 pr-10">
                    <center>
                        <Typography variant="h2">
                            MAKE AN OFFER
                        </Typography>
                    </center>

                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        validationSchema={validationSchema}
                        onSubmit={(values, actions) => {
                            actions.setSubmitting(true);
                            const selectedShipping = transportProvider.find((transport) => transport.value === values.transProvider);
                            const transportationCost = (selectedShipping.label?.split('-')).length > 2 ? selectedShipping.label?.split('-')[2] : selectedShipping.label?.split('-')[1];
                            const transportationType = (selectedShipping.value?.split('-')).length > 1 ? selectedShipping.value?.split('-')[2] : '';
                            const shipperChoice = values.transProvider.includes('transport-reinvent') ? 'reinvent' : 'other';
                            const cleanTitle = 'YES';
                            const costPerMile = 0;
                            const data = {
                                inventoryId: token,
                                amount: values.offer,
                                cleanTitle,
                                shipperChoice: shipperChoice,
                                transportationType: transportationType,
                                transportationCost: shipperChoice === 'reinvent' ? Number(transportationCost.replace(/\$/g, '')) : 0,
                                transportationCostPerMile: costPerMile,
                                buyerRooftopId: values.buyerRooftopId,
                                message: values.message
                            };
                            handleSubmit(data);
                            actions.setSubmitting(false);
                        }}>
                        {({ handleChange }) => {
                            return (
                                <Form autoComplete={false}>
                                    <StyledGrid container className={classes.bgCard}>
                                        <Grid item xs={12} className={classes.bottomSpace}>
                                            <center>
                                                <Typography variant="h3">{title}</Typography>
                                            </center>

                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                name='offer'
                                                label='Your Offer'
                                                InputProps={{
                                                    inputComponent: NumberFormatCustom,
                                                }}
                                            />
                                            <center>
                                                <Typography>Minimum offer has been set to 80% of the asking price</Typography>
                                            </center>
                                        </Grid>
                                        <Grid item xs={12} className="pt-10">
                                            <InputLabel id="rooftop-label">Select Rooftop</InputLabel>
                                            <Select
                                                labelId="buyer-rooftop-id-label"
                                                id="buyerRooftopId"
                                                name="buyerRooftopId"
                                                label=""
                                                className="offerDialog-transportProviderSelect"
                                                items={permittedRooftops.map((rooftop) => { return {value: rooftop.dealerRooftopId, label: rooftop.rooftopName }})}
                                                onChange={handleChange}
                                              />
                                        </Grid>
                                        <Grid item xs={12} className="pt-10">
                                            <InputLabel id="transport-provider-label">Message</InputLabel>
                                            <TextField
                                                name="message"
                                                placeholder="Enter a message regarding your offer to the seller here..."
                                                multiline
                                                rows={4}
                                            />
                                        </Grid>
                                        <Grid item xs={12} className="pt-10">
                                            <InputLabel id="transport-provider-label">Select Transport Provider</InputLabel>
                                            <Select
                                                labelId="transport-provider-label"
                                                id="transProvider"
                                                name="transProvider"
                                                label=""
                                                className="offerDialog-transportProviderSelect"
                                                items={transportProvider}
                                            />
                                        </Grid>
                                    </StyledGrid>
                                    <Grid item xs={12}>
                                        <Button
                                            type='submit'
                                            className={classes.btn}
                                        >
                                            Make an Offer
                                        </Button>
                                    </Grid>
                                </Form>
                            )
                        }}
                    </Formik>

                    <Spinner show={submittingMakeOffer || loadingShippingOptions} />
                </DialogContentText>
            </DialogContent>
        </StyledDialog>
    )
}

export default MakeOffer;